import React, { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { ReactComponent as DocumentWithTextIcon } from '$/assets/icons/DocumentWithText.svg';
import Loading from '$/components/loading/Loading';
import { CompanyService } from '$/services';
import { getSFDREnabled } from '$/store/release-feature-flags/selectors';
import { CLIENT_SFDR_VIEW } from '$/core/constants';
import NotFound from '$/pages/not-found';
import * as Bixby from '$/core';
import './index.scss';

interface ICompanyPaiProps {
  currentCompany?: Bixby.Models.Company;
  featureFlags?: string[];
  isSFDREnabled: boolean;
}

const CompanyPai: React.FC<ICompanyPaiProps> = props => {
  const { currentCompany, isSFDREnabled } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [paiDataError, setPaiDataError] = useState(undefined as string | undefined);
  const [paiData, setPaiData] = useState<Bixby.Models.Pai>();

  const featureFlags = useMemo(() => {
    return props.featureFlags as string[];
  }, [props.featureFlags]);

  useEffect(() => {
    const getCompanyPaiDataAsync = async () => {
      setIsLoading(true);
      setPaiDataError(undefined);
      try {
        if (!currentCompany) return;

        const paiData: Bixby.Models.Pai = await CompanyService.getCompanyPaiDataAsync(currentCompany.id);
        setPaiData(paiData);
      } catch (e: any) {
        setPaiDataError('Error retrieving PAI Metrics');
      } finally {
        setIsLoading(false);
      }
    };

    if (!Bixby.Utils.isNullOrEmpty(currentCompany) && isSFDREnabled && featureFlags.includes(CLIENT_SFDR_VIEW)) {
      getCompanyPaiDataAsync();
    }
  }, [featureFlags, isSFDREnabled, currentCompany]);

  if (!isSFDREnabled || !featureFlags.includes(CLIENT_SFDR_VIEW)) {
    return <NotFound />;
  } else if (!Bixby.Utils.isNullOrEmpty(paiDataError)) {
    return <p className="error">{paiDataError}</p>;
  } else if (Bixby.Utils.isNullOrEmpty(currentCompany)) {
    return null;
  } else if (Bixby.Utils.isNullOrEmpty(paiData)) {
    return (
      <section className="company_pai">
        <h2>PAI</h2>
        <span>No PAI metrics available.</span>
      </section>
    );
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else {
    const groups = paiData?.paiGroups;
    return (
      <>
        <section className="company_pai">
          <h2>PAI</h2>
          <div>
            <table className={classnames('table', 'company_pai_table')}>
              <thead>
                <tr className="company_pai_row_header">
                  <th>Category</th>
                  <th>Value</th>
                  <th>Reference Details</th>
                  <th>Evidence Source</th>
                </tr>
              </thead>
              <tbody>
                {groups &&
                  groups.map(group => {
                    return (
                      <>
                        <tr className="company_pai_table_header">
                          <th colSpan={3}>{group.groupName}</th>
                        </tr>
                        {group.items.length > 0 && (
                          <>
                            {group.items.map(item => {
                              const val = item.value ? (item.value.toLowerCase() === 'na' ? 'N/A' : item.value) : 'N/A';
                              return (
                                <tr>
                                  <td>{item.metric}</td>
                                  <td>
                                    <span>{val}</span>
                                  </td>
                                  <td>
                                    {item.imageUrl && (
                                      <a className="evidence" target="_blank" href={item.imageUrl}>
                                        <span>{item.evidence}</span>
                                        <div className="icon">
                                          <DocumentWithTextIcon />
                                        </div>
                                      </a>
                                    )}
                                    {!item.imageUrl && <span>{item.evidence}</span>}
                                  </td>
                                  <td>{item.evidenceSource}</td>
                                </tr>
                              );
                            })}
                          </>
                        )}
                      </>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  }
};
const mapStateToProps = (state: AppState) => {
  const { isSFDREnabled } = getSFDREnabled(state);

  return {
    currentCompany: state.portfolioState.currentCompany,
    featureFlags: state.loginState.featureFlags,
    isSFDREnabled,
  };
};

export default connect(mapStateToProps)(CompanyPai);
